import { Box, Button, Header, Modal, SpaceBetween, Table } from '@cloudscape-design/components';
import { useEffect, useState } from 'react';
import { fetchItemsWithFilter } from '../api/generic';
import { useAuthContext } from '../contexts/AuthContext';
import type { Batch } from '../models/Batch';
import { prefixCollection } from '../utils/prefix';
import CreateNewBatchModal from './CreateNewBatchModal';

export function BatchChooserModal({
  visible,
  onDismiss,
  onConfirm,
}: {
  visible: boolean;
  onDismiss: () => void;
  onConfirm: (selected: Batch[]) => Promise<void>;
}) {
  const { tenant } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Batch[]>([]);
  const [selected, setSelected] = useState<Batch[]>([]);
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    setLoading(true);
    const list = await fetchItemsWithFilter<Batch>(prefixCollection('batches', tenant), {
      or: [
        { field: 'status', operator: '==', value: 'planned' },
        { field: 'status', operator: '==', value: 'in-progress' },
      ],
    });

    console.log('Loaded batches for brewing', list);
    setRows(list ?? []);
    setLoading(false);
  }

  useEffect(() => {
    if (visible) load();
  }, [tenant, visible]);

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={<Header actions={<CreateNewBatchModal onBatchCreated={load} />}>Select batch(es) for this Brew</Header>}
      size="large"
      footer={
        <Box float="right">
          <SpaceBetween size="xs" direction="horizontal">
            <Button variant="link" onClick={onDismiss} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={selected.length === 0 || submitting}
              loading={submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  await onConfirm(selected);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Start Brew
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Table
        wrapLines
        loading={loading}
        loadingText="Loading batches"
        variant="embedded"
        items={rows}
        selectionType="multi"
        selectedItems={selected}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems as Batch[])}
        columnDefinitions={[
          { id: 'name', header: 'Batch', cell: (r: Batch) => r.name },
          { id: 'status', header: 'Status', cell: (r: Batch) => r.status },
          {
            id: 'volume',
            header: 'Planned L',
            cell: (r: Batch) => (r as any).plannedLiters ?? '-',
          },
          {
            id: 'recipe',
            header: 'Recipe',
            cell: (r: Batch) => r.recipe?.name ?? r.recipeId ?? '-',
          },
        ]}
        header={<Header counter={rows.length ? `(${rows.length})` : ''}>Eligible batches</Header>}
        empty={
          <Box textAlign="center" color="inherit">
            <b>No batches to brew.</b>
            <Box variant="p">Create a planned or in-progress batch first.</Box>
          </Box>
        }
      />
    </Modal>
  );
}
